/**
 * Cache Management Settings Component
 * Unified cache management combining encrypted storage and offline sync
 */

import { useState, useEffect, useCallback } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faDatabase,
  faTrash,
  faInfoCircle,
  faExclamationTriangle,
  faLock,
  faDownload,
  faSpinner,
  faCheck,
  faTimes,
  faFlask,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons'
import { useAuthContext } from '../../contexts/AuthContext'
import { encryptedDeviceCacheService, CacheStats } from '../../services/cache/encryptedDeviceCacheService'
import { encryptionTestService } from '../../services/cache/encryptionTestService'
import { readingsCacheService } from '../../services/cache/readingsCacheService'
import { cacheSettingsService } from '../../services/cache/cacheSettingsService'
import { ReadingsCacheStats } from '../../utils/encryptedReadingsCache'

export function OfflineCacheSettings() {
  const { email } = useAuthContext()
  const [cacheStats, setCacheStats] = useState<CacheStats | null>(null)
  const [userStats, setUserStats] = useState<{
    hasCache: boolean
    deviceCount: number
    lastUpdated: number | null
  } | null>(null)
  const [readingsStats, setReadingsStats] = useState<ReadingsCacheStats | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [lastCleared, setLastCleared] = useState<string | null>(null)
  const [testResults, setTestResults] = useState<any>(null)
  const [showTestResults, setShowTestResults] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const [encryptionInfo, setEncryptionInfo] = useState<any>(null)
  const [isOnline] = useState(navigator.onLine)
  const [cacheEnabled, setCacheEnabled] = useState(() => cacheSettingsService.isCacheEnabled())
  const [encryptionEnabled, setEncryptionEnabled] = useState(() => cacheSettingsService.isEncryptionEnabled())

  // Load cache statistics
  useEffect(() => {
    loadCacheStats()
    loadEncryptionInfo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email])

  const loadCacheStats = async () => {
    if (!email) return

    setIsLoading(true)
    try {
      const [stats, userCacheStats, readingsStats] = await Promise.all([
        encryptedDeviceCacheService.getCacheStats(),
        encryptedDeviceCacheService.getUserCacheStats(email),
        readingsCacheService.getCacheStats(email),
      ])

      setCacheStats(stats)
      setUserStats(userCacheStats)
      setReadingsStats(readingsStats)
    } catch (error) {
      console.error('Failed to load cache stats:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadEncryptionInfo = () => {
    const info = encryptedDeviceCacheService.getEncryptionInfo()
    setEncryptionInfo(info)
  }

  const clearEncryptedCache = async () => {
    if (!email) return

    setActionLoading('clear-encrypted')
    try {
      await Promise.all([
        encryptedDeviceCacheService.clearUserCache(email),
        readingsCacheService.invalidateUser(email),
      ])
      setLastCleared(`Encrypted cache cleared at ${new Date().toLocaleTimeString()}`)
      await loadCacheStats()
    } catch (error) {
      console.error('Failed to clear encrypted cache:', error)
      setLastCleared(`Failed to clear cache: ${error}`)
    } finally {
      setActionLoading(null)
    }
  }

  const clearAllCache = async () => {
    if (!email) return

    setShowConfirmDialog(false)
    setActionLoading('clear-all')
    try {
      await Promise.all([
        encryptedDeviceCacheService.clearAllCache(),
        readingsCacheService.clearAllCache(),
      ])
      setLastCleared(`All cache cleared at ${new Date().toLocaleTimeString()}`)
      await loadCacheStats()
    } catch (error) {
      console.error('Failed to clear all cache:', error)
      setLastCleared(`Failed to clear cache: ${error}`)
    } finally {
      setActionLoading(null)
    }
  }

  const exportCacheData = async () => {
    if (!email) return

    setActionLoading('export')
    try {
      const exportData = {
        user: email,
        timestamp: new Date().toISOString(),
        deviceCacheStats: cacheStats,
        userStats: userStats,
        readingsCacheStats: readingsStats,
        encryptionInfo: encryptionInfo,
        testResults: testResults,
      }

      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `ioag-cache-stats-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setLastCleared(`Statistics exported at ${new Date().toLocaleTimeString()}`)
    } catch (error) {
      console.error('Failed to export cache data:', error)
    } finally {
      setActionLoading(null)
    }
  }

  const runSecurityTests = async () => {
    setActionLoading('security-tests')
    try {
      const results = await encryptionTestService.runCompleteTestSuite()
      setTestResults(results)
      setShowTestResults(true)
      setLastCleared(`Security tests completed at ${new Date().toLocaleTimeString()}`)
      await loadCacheStats()
    } catch (error) {
      console.error('Failed to run security tests:', error)
    } finally {
      setActionLoading(null)
    }
  }

  const cleanupTestData = async () => {
    setActionLoading('cleanup-tests')
    try {
      await encryptionTestService.cleanupTestData()
      setTestResults(null)
      setLastCleared(`Test data cleaned up at ${new Date().toLocaleTimeString()}`)
      await loadCacheStats()
    } catch (error) {
      console.error('Failed to cleanup test data:', error)
    } finally {
      setActionLoading(null)
    }
  }

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId)
      } else {
        newSet.add(sectionId)
      }
      return newSet
    })
  }, [])

  const handleToggleCache = (enabled: boolean) => {
    cacheSettingsService.setCacheEnabled(enabled)
    setCacheEnabled(enabled)
    setLastCleared(`Cache ${enabled ? 'enabled' : 'disabled'} at ${new Date().toLocaleTimeString()}`)
  }

  const handleToggleEncryption = (enabled: boolean) => {
    if (!enabled) {
      // Warn user about security implications
      const confirmed = window.confirm(
        'Disabling encryption will store data in plain text for better performance. ' +
        'This is less secure. Continue?'
      )
      if (!confirmed) {
        // User cancelled - don't change setting
        return
      }
    }

    cacheSettingsService.setEncryptionEnabled(enabled)
    setEncryptionEnabled(enabled)
    setLastCleared(`Encryption ${enabled ? 'enabled' : 'disabled'} at ${new Date().toLocaleTimeString()}`)
  }

  const isEncryptionHealthy = cacheStats?.encryptionHealth?.encryptionServiceAvailable
  const totalReadings = readingsStats?.totalReadings || 0
  const deviceCount = userStats?.deviceCount || 0

  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FontAwesomeIcon icon={faDatabase} className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Cache Management</h3>
              <p className="text-sm text-gray-600">Manage your encrypted cache and offline data</p>
            </div>
          </div>
          {/* Online Status Indicator */}
          <div className="flex items-center space-x-2">
            <div className={`h-2 w-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="text-sm font-medium text-gray-700">{isOnline ? 'Online' : 'Offline'}</span>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="p-6 text-center">
          <FontAwesomeIcon icon={faSpinner} className="h-6 w-6 text-blue-600 animate-spin mb-2" />
          <p className="text-gray-600">Loading cache statistics...</p>
        </div>
      )}

      {/* Content */}
      {!isLoading && (
        <div className="p-6 space-y-6">
          {/* Cache Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border-2 border-blue-200">
            <div className="flex-1">
              <h4 className="font-medium text-gray-900">Enable Data Cache</h4>
              <p className="text-sm text-gray-600">
                Store encrypted data locally for faster loading and offline access
              </p>
            </div>
            <button
              onClick={() => handleToggleCache(!cacheEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                cacheEnabled ? 'bg-blue-600' : 'bg-gray-300'
              }`}
              aria-label={`${cacheEnabled ? 'Disable' : 'Enable'} cache`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  cacheEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Cache Disabled Warning */}
          {!cacheEnabled && (
            <div className="p-4 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-yellow-600" />
                <p className="text-sm text-yellow-800">
                  Cache is currently disabled. Enable cache to see statistics and improve performance.
                </p>
              </div>
            </div>
          )}

          {/* Encryption Enable/Disable Toggle */}
          {cacheEnabled && (
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border-2 border-purple-200">
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">Enable Cache Encryption</h4>
                <p className="text-sm text-gray-600">
                  Encrypt cached data with AES-256-CBC (more secure, slightly slower)
                </p>
              </div>
              <button
                onClick={() => handleToggleEncryption(!encryptionEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  encryptionEnabled ? 'bg-purple-600' : 'bg-gray-300'
                }`}
                aria-label={`${encryptionEnabled ? 'Disable' : 'Enable'} encryption`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    encryptionEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )}

          {/* Encryption Disabled Warning */}
          {cacheEnabled && !encryptionEnabled && (
            <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-red-600" />
                <p className="text-sm text-red-800">
                  <strong>Warning:</strong> Cache encryption is disabled. Data is stored in plain text.
                </p>
              </div>
            </div>
          )}

          {/* Encryption Status */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <FontAwesomeIcon
                icon={encryptionEnabled && isEncryptionHealthy ? faLock : faExclamationTriangle}
                className={`h-4 w-4 mr-3 ${
                  encryptionEnabled && isEncryptionHealthy ? 'text-green-600' : 'text-yellow-600'
                }`}
              />
              <div>
                <h4 className="font-medium text-gray-900">Encryption Status</h4>
                <p className="text-sm text-gray-600">
                  {encryptionEnabled
                    ? `${encryptionInfo?.algorithm || 'AES-256-CBC'} with ${encryptionInfo?.keySize || 256}-bit keys`
                    : 'Encryption disabled - data stored in plain text'}
                </p>
              </div>
            </div>
            <span
              className={`px-3 py-1 text-xs font-semibold rounded-full ${
                encryptionEnabled && isEncryptionHealthy
                  ? 'bg-green-100 text-green-800'
                  : encryptionEnabled
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {encryptionEnabled
                ? isEncryptionHealthy
                  ? 'Active'
                  : 'Initializing...'
                : 'Disabled'}
            </span>
          </div>

          {/* Cache Statistics Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border-2 border-blue-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{deviceCount.toLocaleString()}</div>
              <div className="text-sm text-gray-600 mt-1">Devices</div>
            </div>
            <div className="bg-white border-2 border-green-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{totalReadings.toLocaleString()}</div>
              <div className="text-sm text-gray-600 mt-1">Readings</div>
            </div>
            <div className="bg-white border-2 border-purple-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">
                {readingsStats?.totalRanges?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-gray-600 mt-1">Time Ranges</div>
            </div>
            <div className="bg-white border-2 border-indigo-200 rounded-lg p-4 text-center">
              <div className="text-lg font-bold text-gray-900">
                {userStats?.lastUpdated ? new Date(userStats.lastUpdated).toLocaleTimeString() : 'Never'}
              </div>
              <div className="text-sm text-gray-600 mt-1">Last Updated</div>
            </div>
          </div>

          {/* Detailed Cache Breakdown */}
          {readingsStats && readingsStats.totalReadings > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Date Range Coverage</h4>
              <div className="text-sm text-gray-700">
                {readingsStats.oldestTimestamp && readingsStats.newestTimestamp && (
                  <p>
                    {new Date(readingsStats.oldestTimestamp * 1000).toLocaleDateString()} -{' '}
                    {new Date(readingsStats.newestTimestamp * 1000).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Cache Management Actions */}
          <div className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleSection('cache-actions')}
              className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-t-lg transition-colors"
            >
              <h4 className="font-medium text-gray-900">Cache Management Actions</h4>
              <FontAwesomeIcon
                icon={expandedSections.has('cache-actions') ? faChevronUp : faChevronDown}
                className="h-4 w-4 text-gray-600"
              />
            </button>

            {expandedSections.has('cache-actions') && (
              <div className="p-4 space-y-3">
                <button
                  onClick={clearEncryptedCache}
                  disabled={actionLoading !== null}
                  className="w-full px-4 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Clear your encrypted cache"
                >
                  {actionLoading === 'clear-encrypted' ? (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faTrash} className="mr-2 h-4 w-4" />
                      Clear My Cache
                    </>
                  )}
                </button>

                <button
                  onClick={() => setShowConfirmDialog(true)}
                  disabled={actionLoading !== null}
                  className="w-full px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Clear all cache"
                >
                  {actionLoading === 'clear-all' ? (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faTrash} className="mr-2 h-4 w-4" />
                      Clear All Cache (Admin)
                    </>
                  )}
                </button>

                <button
                  onClick={exportCacheData}
                  disabled={actionLoading !== null}
                  className="w-full px-4 py-2.5 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Export cache statistics"
                >
                  {actionLoading === 'export' ? (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faDownload} className="mr-2 h-4 w-4" />
                      Export Statistics
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Developer Tools */}
          <div className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleSection('dev-tools')}
              className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-t-lg transition-colors"
            >
              <div className="flex items-center">
                <FontAwesomeIcon icon={faFlask} className="h-4 w-4 text-gray-600 mr-2" />
                <h4 className="font-medium text-gray-900">Developer & Testing Tools</h4>
              </div>
              <FontAwesomeIcon
                icon={expandedSections.has('dev-tools') ? faChevronUp : faChevronDown}
                className="h-4 w-4 text-gray-600"
              />
            </button>

            {expandedSections.has('dev-tools') && (
              <div className="p-4 space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                  <p className="text-sm text-amber-800">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                    These tools are for testing encryption and cache functionality.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={runSecurityTests}
                    disabled={actionLoading !== null}
                    className="px-4 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Run security tests"
                  >
                    {actionLoading === 'security-tests' ? (
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faFlask} className="mr-2" />
                        Run Security Tests
                      </>
                    )}
                  </button>

                  <button
                    onClick={cleanupTestData}
                    disabled={actionLoading !== null}
                    className="px-4 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Cleanup test data"
                  >
                    {actionLoading === 'cleanup-tests' ? (
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faTimes} className="mr-2" />
                        Cleanup Test Data
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Action Result Message */}
          {lastCleared && (
            <div
              className={`flex items-center space-x-2 text-sm rounded-lg p-3 ${
                lastCleared.includes('Failed') ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50'
              }`}
              role="alert"
            >
              <FontAwesomeIcon icon={lastCleared.includes('Failed') ? faTimes : faCheck} className="h-4 w-4" />
              <span>{lastCleared}</span>
            </div>
          )}

          {/* Test Results */}
          {testResults && showTestResults && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <FontAwesomeIcon icon={faFlask} className="h-4 w-4 mr-2 text-purple-600" />
                  Security & Performance Test Results
                </h4>
                <button
                  onClick={() => setShowTestResults(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close test results"
                >
                  <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-2">
                {Object.entries(testResults).map(([key, result]: [string, any]) => (
                  <div key={key} className="flex items-center justify-between p-2 bg-white rounded border">
                    <div className="flex items-center space-x-3">
                      <FontAwesomeIcon
                        icon={result.success ? faCheck : faTimes}
                        className={`h-4 w-4 ${result.success ? 'text-green-600' : 'text-red-600'}`}
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">{result.testName}</span>
                        {result.error && <p className="text-xs text-red-600">{result.error}</p>}
                      </div>
                    </div>
                    <span className="text-xs text-gray-600">{result.duration}ms</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Information */}
          <div className="border-l-4 border-blue-500 bg-blue-50 rounded-r-lg p-5">
            <div className="flex items-start space-x-3">
              <FontAwesomeIcon icon={faInfoCircle} className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-blue-900 mb-2">About Cache & Offline Access</h4>
                <div className="text-sm text-blue-800 space-y-1.5">
                  <p>All data is encrypted with AES-256-CBC using your unique account key</p>
                  <p>Cache enables offline access when internet is unavailable</p>
                  <p>Device metadata and sensor readings are cached separately for optimal performance</p>
                  <p>Smart caching only fetches new data, reducing API calls by 70-90%</p>
                  <p>Only you can decrypt and access your cached data with your credentials</p>
                </div>
              </div>
            </div>

            {!isOnline && (
              <div className="mt-4 flex items-start space-x-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-semibold text-yellow-900 mb-1">Offline Mode</h4>
                  <p className="text-sm text-yellow-800">
                    You are currently offline. Using cached data. Some features may be limited until connectivity is
                    restored.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Clear All Cache?</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will remove all encrypted cache data for all users. This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={clearAllCache}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
